import type { ICommand } from '../../../core/commands/ICommand';
import { getNodeMutationManager } from '../../../core/commands/mutations/NodeMutationManager';
import {
  borderColorForContainer,
  type FrameDescriptor,
} from '../canvas/containerFrames';

/**
 * CreateContainerFrameCommand — DL-2.5.
 *
 * Writes a single container Frame to the diagram's Y.Doc `frames` map.
 * Mirrors what `hydrateImportedResources.buildContainerFrames` does
 * for imported AWS scans, but as an undoable `ICommand` so chat
 * tools (apply_pattern, compose_service) can compose Frames into the
 * same CompositeCommand that builds their leaf shapes.
 *
 * Undo: removes the frame from the Y.Map. Frame children (the leaf
 * shapes) are owned by their own CreateNetworkResourceCommand siblings
 * in the composite and are undone separately — composite reverse-order
 * undo handles the orchestration.
 *
 * Why direct Y.Map writes instead of going through a higher-level
 * mutation manager API: the FrameManager bootstraps from `getMap('frames')`
 * + listens for updates. Writing through the same map is the standard
 * interop path (compass-explorer's lock-synthesized scene and hydrate's
 * Pass 3 both use it). A future refactor could move this into
 * NodeMutationManager once Frame mutations need batching.
 */
export interface CreateContainerFrameParams {
  descriptor: FrameDescriptor;
  /** Optional resource attrs to expose under frame.metadata (region, tags, …). */
  metadata?: Record<string, unknown>;
}

export class CreateContainerFrameCommand implements ICommand {
  id: string;
  name = 'Create Container Frame';
  description?: string;
  timestamp: number;
  private params: CreateContainerFrameParams;

  constructor(params: CreateContainerFrameParams) {
    this.params = params;
    this.id = `create-container-frame-${params.descriptor.id}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    this.timestamp = Date.now();
    this.description = `Create container frame ${params.descriptor.id}`;
  }

  execute(): void {
    const doc = getNodeMutationManager().getDoc();
    if (!doc) {
      // No Y.Doc attached — execution is a no-op. The canvas might
      // be running in a test or detached mode; the composite still
      // succeeds so leaf shapes land. Frames will simply be absent.
      return;
    }
    const frames = doc.getMap<Record<string, unknown>>('frames');
    const { descriptor } = this.params;
    // Branded AWS frame styling — FrameRenderer reads these
    // `metadata.pillTone` / `pillGlyph` / `borderDash` hooks to
    // render the colored container look (purple VPC, teal public
    // subnet, blue private subnet) instead of the neutral gray
    // outline. Without this, compose frames render as thin gray
    // outlines that look unprofessional in client previews.
    const { pillTone, pillGlyph, borderDash } = stylingForContainer(descriptor.manifestId);
    doc.transact(() => {
      frames.set(descriptor.id, {
        id: descriptor.id,
        name: descriptor.name,
        type: 'generic',
        bounds: descriptor.bounds,
        shapes: descriptor.shapes,
        connectors: [],
        parentFrame: descriptor.parentFrame,
        visible: true,
        locked: false,
        borderColor: borderColorForContainer(descriptor.manifestId),
        borderWidth: 2,
        opacity: 1,
        metadata: {
          source: 'network-design',
          containerType: descriptor.manifestId,
          pillTone,
          ...(pillGlyph ? { pillGlyph } : {}),
          ...(borderDash ? { borderDash } : {}),
          ...(this.params.metadata ?? {}),
        },
      });
    }, 'network-design-frame-create');
  }

  undo(): void {
    const doc = getNodeMutationManager().getDoc();
    if (!doc) return;
    const frames = doc.getMap('frames');
    doc.transact(() => {
      frames.delete(this.params.descriptor.id);
    }, 'network-design-frame-undo');
  }

  redo(): void {
    this.execute();
  }
}

/**
 * Map AWS container manifest ids to FrameRenderer's branded styling
 * vocabulary (pillTone / pillGlyph / borderDash). Mirrors what the
 * Compass explorer view materializer stamps so chat-composed frames
 * render with the same visual identity as imported AWS scans.
 */
function stylingForContainer(manifestId: string): {
  pillTone: string;
  pillGlyph?: string;
  borderDash?: number[];
} {
  switch (manifestId) {
    case 'aws_vpc':
      return { pillTone: 'aws-vpc', pillGlyph: 'V', borderDash: [8, 4] };
    case 'aws_subnet_public':
      return { pillTone: 'aws-subnet-public', pillGlyph: 'S' };
    case 'aws_subnet_private':
      return { pillTone: 'aws-subnet-private', pillGlyph: 'S' };
    case 'aws_subnet':
      return { pillTone: 'aws-subnet', pillGlyph: 'S' };
    case 'aws_account':
      return { pillTone: 'aws-account', pillGlyph: 'A' };
    case 'aws_region':
      return { pillTone: 'neutral', pillGlyph: 'R' };
    default:
      return { pillTone: 'neutral' };
  }
}
